import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useAccessLink } from '@/hooks/useAccessLink';

/**
 * Access link landing page.
 * Handles /access/:code URLs to grant users access to masterclasses.
 *
 * Flow:
 * 1. Extract code from URL params
 * 2. Validate code via REST API
 * 3. Create guest account if user not authenticated
 * 4. Redirect to cases on success
 */
const AccessLink = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { handleAccessLink, loading, error } = useAccessLink();
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    if (!code) {
      navigate(ROUTES.AUTH);
      return;
    }

    if (processed) return;

    const validateAccess = async () => {
      try {
        await handleAccessLink(code);
        setProcessed(true);
      } catch (err) {
        console.error('Access link validation failed:', err);
        // Error state handled by hook
      }
    };

    void validateAccess();
  }, [code, processed, handleAccessLink, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-lg">Validating access...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-500">Invalid access link</p>
          <p className="mt-2 text-sm text-gray-500">{error.message}</p>
          <button
            className="mt-4 rounded bg-primary px-4 py-2 text-white"
            onClick={() => navigate(ROUTES.AUTH)}
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return null; // Will redirect on success
};

export default AccessLink;
